/**
 * Report Generation Service
 *
 * Generates structured report artifacts from completed sessions using
 * a report template. For AI-generated sections, calls the LLM explainer.
 * For data sections, reads directly from the session report.
 *
 * Source: DOCS_V4/00-canon/10O-cursor-session-results-explanation-and-reports.md
 */

import { getSafeSupabaseClient, generateUUID, isSupabaseAvailable } from './supabaseSync';
import { explainForMemo, explainResults } from './llmResultsExplainerService';
import type { ResultsExplainerContext, ResultsExplanation } from './llmResultsExplainerService';
import { getTypedSupabaseClient } from './typedSupabaseClient';
import { isUuid } from './isUuid';
import { sessionOwnItemTitles } from './sessions/sessionItemTitles';
import { getToolById } from '../data/prioritization/tools';
import { resolveSessionToolDescriptor } from './sessionToolCatalog';
import { loadSessionTranscript } from './sessionTranscript';
import {
  MEMO_TEMPLATE_SLUG,
  assembleMemo,
  buildMemoPrompt,
  humanizeCriterionId,
  parseMemoExplanation,
  splitAtCutLine,
  type MemoSourceRanking,
  type MemoSources,
} from './prioritizationMemo';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ReportSection {
  id: string;
  label: string;
  type:
    | 'headline'
    | 'ranked_list'
    | 'ai_narrative'
    | 'relationship_notes'
    | 'session_metadata'
    | 'context_header'
    | 'text'
    | 'contact';
  limit?: number;
  above_line_only?: boolean;
  show_scores?: boolean;
  show_cut_line?: boolean;
  prompt_focus?: 'executive' | 'alignment' | 'stakeholder';
  content?: string;
}

export interface ReportTemplate {
  id: string;
  name: string;
  slug: string;
  description?: string;
  audience: string;
  tone: string;
  isSystem: boolean;
  sections: ReportSection[];
}

export interface ReportArtifact {
  id: string;
  sessionId: string;
  templateId?: string;
  templateSlug: string;
  workspaceId?: string;
  title: string;
  content: Record<string, unknown>;
  generatedAt: Date;
  generatedBy: string;
  status: 'draft' | 'published';
}

// ── System template definitions (client-side fallback) ────────────────────────

export const SYSTEM_REPORT_TEMPLATES: ReportTemplate[] = [
  {
    id: 'system-executive_summary',
    name: 'Executive Summary',
    slug: 'executive_summary',
    description: 'A concise summary for leadership — what was decided and what matters.',
    audience: 'leadership',
    tone: 'concise',
    isSystem: true,
    sections: [
      { id: 'headline', label: 'Decision', type: 'headline' },
      { id: 'top_items', label: 'Top Priorities', type: 'ranked_list', limit: 3, above_line_only: true },
      { id: 'key_insight', label: 'Key Insight', type: 'ai_narrative', prompt_focus: 'executive' },
      { id: 'next_steps', label: 'Next Steps', type: 'text' },
    ],
  },
  {
    id: 'system-participant_debrief',
    name: 'Participant Debrief',
    slug: 'participant_debrief',
    description: 'Full results for session participants — rankings, scores, group alignment.',
    audience: 'participants',
    tone: 'detailed',
    isSystem: true,
    sections: [
      { id: 'full_ranking', label: 'Full Ranking', type: 'ranked_list', show_scores: true, show_cut_line: true },
      { id: 'alignment', label: 'Group Alignment', type: 'ai_narrative', prompt_focus: 'alignment' },
      { id: 'relationships', label: 'Item Relationships', type: 'relationship_notes' },
      { id: 'methodology', label: 'Methodology', type: 'session_metadata' },
    ],
  },
  {
    id: 'system-stakeholder_communication',
    name: 'Stakeholder Communication',
    slug: 'stakeholder_communication',
    description: 'A plain-language summary for people who were not in the session.',
    audience: 'stakeholders',
    tone: 'professional',
    isSystem: true,
    sections: [
      { id: 'context', label: 'What We Decided', type: 'context_header' },
      { id: 'results', label: 'Results', type: 'ranked_list', above_line_only: true },
      { id: 'rationale', label: 'Why These Priorities', type: 'ai_narrative', prompt_focus: 'stakeholder' },
      { id: 'implications', label: 'What This Means', type: 'text' },
      { id: 'contact', label: 'Questions?', type: 'contact' },
    ],
  },
];

// ── Section content builders ──────────────────────────────────────────────────

function extractAiNarrativeContent(
  section: ReportSection,
  explanation: ResultsExplanation | null
): string {
  if (!explanation) return '';
  switch (section.prompt_focus) {
    case 'executive':
      return [explanation.executiveSummary, ...(explanation.keyInsights?.slice(0, 2) ?? [])].join('\n');
    case 'alignment':
      return explanation.participantAlignment ?? explanation.executiveSummary ?? '';
    case 'stakeholder':
      return [explanation.executiveSummary, explanation.relationshipNotes]
        .filter(Boolean)
        .join('\n\n');
    default:
      return explanation.executiveSummary ?? '';
  }
}

function buildRankedListContent(
  section: ReportSection,
  ctx: ResultsExplainerContext
): string {
  let items = [...ctx.rankedItems];

  if (section.above_line_only && ctx.cutLinePosition) {
    items = items.filter(i => i.rank <= ctx.cutLinePosition!);
  }

  if (section.limit) {
    items = items.slice(0, section.limit);
  }

  return items
    .map(item => {
      const scoreStr = section.show_scores && item.score !== undefined
        ? ` (score: ${item.score.toFixed(2)})`
        : '';
      const cutStr =
        section.show_cut_line &&
        ctx.cutLinePosition &&
        item.rank === ctx.cutLinePosition
          ? ' ← cut line'
          : '';
      return `#${item.rank}. ${item.title}${scoreStr}${cutStr}`;
    })
    .join('\n');
}

function buildRelationshipNotesContent(ctx: ResultsExplainerContext): string {
  if (!ctx.activeRelationships.length) return 'No item relationships recorded for this session.';
  return ctx.activeRelationships
    .map(r => `"${r.sourceTitle}" → ${r.relationshipType} → "${r.targetTitle}" [${r.status}]`)
    .join('\n');
}

function buildSessionMetadataContent(ctx: ResultsExplainerContext): string {
  const parts: string[] = [
    `Tool: ${ctx.toolName}`,
    `Participants: ${ctx.participantCount}`,
    `Items ranked: ${ctx.totalItems}`,
  ];
  if (ctx.criteriaIds.length) {
    parts.push(`Criteria: ${ctx.criteriaIds.map(id => ctx.criteriaLabels?.[id] ?? id).join(', ')}`);
  }
  if (ctx.cycleName) parts.push(`Cycle: ${ctx.cycleName}`);
  if (ctx.trackName) parts.push(`Track: ${ctx.trackName}`);
  if (ctx.completedAt) parts.push(`Completed: ${ctx.completedAt}`);
  return parts.join('\n');
}

// ── Main generator ────────────────────────────────────────────────────────────

/**
 * Generate a report artifact for a completed session using the given template.
 * Stores the artifact in `report_artifacts` and returns the artifact id.
 *
 * Throws if generation fails — callers must handle and surface the error.
 */
export async function generateReportArtifact(
  sessionId: string,
  template: ReportTemplate,
  explainerContext: ResultsExplainerContext,
  actorId: string,
  workspaceId?: string
): Promise<ReportArtifact> {
  // Generate LLM explanation once (used by multiple ai_narrative sections)
  let explanation: ResultsExplanation | null = null;
  const hasAiSection = template.sections.some(s => s.type === 'ai_narrative');
  if (hasAiSection) {
    try {
      explanation = await explainResults(explainerContext);
    } catch (e) {
      console.warn('[reportGenerationService] LLM explanation failed, continuing with data-only sections:', e);
    }
  }

  // Build section content
  const sectionContents: Record<string, string> = {};

  for (const section of template.sections) {
    switch (section.type) {
      case 'headline':
        sectionContents[section.id] = explanation?.headline ?? explainerContext.sessionName;
        break;
      case 'context_header':
        sectionContents[section.id] = [
          explainerContext.sessionName,
          explainerContext.cycleName && `Cycle: ${explainerContext.cycleName}`,
          explainerContext.trackName && `Track: ${explainerContext.trackName}`,
        ]
          .filter(Boolean)
          .join('\n');
        break;
      case 'ranked_list':
        sectionContents[section.id] = buildRankedListContent(section, explainerContext);
        break;
      case 'ai_narrative':
        sectionContents[section.id] = extractAiNarrativeContent(section, explanation);
        break;
      case 'relationship_notes':
        sectionContents[section.id] = buildRelationshipNotesContent(explainerContext);
        break;
      case 'session_metadata':
        sectionContents[section.id] = buildSessionMetadataContent(explainerContext);
        break;
      case 'text':
      case 'contact':
        sectionContents[section.id] = ''; // user fills in after generation
        break;
    }
  }

  const artifact: ReportArtifact = {
    id: generateUUID(),
    sessionId,
    templateId: template.isSystem ? undefined : template.id,
    templateSlug: template.slug,
    workspaceId,
    title: `${template.name} — ${explainerContext.sessionName}`,
    content: {
      templateName: template.name,
      templateSlug: template.slug,
      audience: template.audience,
      tone: template.tone,
      sections: template.sections.map(s => ({
        ...s,
        content: sectionContents[s.id] ?? '',
      })),
      explanationHeadline: explanation?.headline,
      keyInsights: explanation?.keyInsights,
    },
    generatedAt: new Date(),
    generatedBy: actorId,
    status: 'draft',
  };

  const persistError = await persistReportArtifact(artifact);
  if (persistError) {
    console.error('[reportGenerationService] Failed to persist artifact:', persistError);
    // Don't throw — still return the in-memory artifact so the UI can show it
  }

  return artifact;
}

/** The one insert into report_artifacts. Returns the database's error message, or null. */
async function persistReportArtifact(artifact: ReportArtifact): Promise<string | null> {
  if (!isSupabaseAvailable()) return 'Supabase is not available';
  const supabase = getSafeSupabaseClient();
  if (!supabase) return 'Supabase is not available';
  const { error } = await supabase.from('report_artifacts').insert({
    id: artifact.id,
    session_id: artifact.sessionId,
    template_id: artifact.templateId ?? null,
    template_slug: artifact.templateSlug,
    workspace_id: artifact.workspaceId ?? null,
    title: artifact.title,
    content: artifact.content,
    generated_at: artifact.generatedAt.toISOString(),
    generated_by: artifact.generatedBy,
    status: artifact.status,
  });
  return error ? error.message : null;
}

// ── The prioritization memo (Brief K slice 3; assembly in prioritizationMemo.ts) ──

function asNumber(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (v && typeof v === 'object' && typeof (v as { total?: unknown }).total === 'number') {
    return (v as { total: number }).total;
  }
  return null;
}

/**
 * Everything the memo needs, read from the record by session id, so the publish
 * step, a Regenerate button and (later) a server job all write the same memo.
 * Returns null when the session has no ranking to explain.
 */
export async function loadMemoSources(sessionId: string): Promise<MemoSources | null> {
  if (!isSupabaseAvailable()) return null;
  const db = getTypedSupabaseClient();

  const { data: session, error: sessionError } = await db
    .from('prioritization_sessions')
    .select('id, name, workspace_id, cycle_id, track_id, selected_tool_id, criteria_config, cut_line_position, ranking_output, participant_ids, published_at, mode_config, isolated_items')
    .eq('id', sessionId)
    .maybeSingle();
  if (sessionError || !session) return null;

  const { data: report } = await db
    .from('session_reports')
    .select('outputs_snapshot, inputs_snapshot')
    .eq('session_id', sessionId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  // The order as published (the session report), else the session's own output.
  const output = (session.ranking_output ?? {}) as {
    rankedItemIds?: string[];
    scores?: Record<string, unknown>;
    itemNotes?: Record<string, string>;
    toolId?: string;
    toolName?: string;
  };
  const snapshot = (report?.outputs_snapshot ?? {}) as { ranking?: Array<{ itemId: string; rank: number; score?: unknown }>; cutLinePosition?: number };
  let ranking: MemoSourceRanking[] = [];
  if (Array.isArray(snapshot.ranking) && snapshot.ranking.length > 0) {
    ranking = snapshot.ranking
      .filter((r) => r && typeof r.itemId === 'string')
      .map((r) => ({ itemId: r.itemId, rank: Number(r.rank), score: asNumber(r.score) }));
  } else if (Array.isArray(output.rankedItemIds)) {
    ranking = output.rankedItemIds.map((id, i) => ({ itemId: id, rank: i + 1, score: asNumber(output.scores?.[id]) }));
  }
  if (ranking.length === 0) return null;
  ranking.sort((a, b) => a.rank - b.rank);
  const cutLine = session.cut_line_position ?? (typeof snapshot.cutLinePosition === 'number' ? snapshot.cutLinePosition : null);

  const { above, nearMisses } = splitAtCutLine(ranking, cutLine);
  const allIds = ranking.map((r) => r.itemId);
  const coveredIds = [...above, ...nearMisses].map((r) => r.itemId);

  const config = (session.criteria_config ?? {}) as { criteriaIds?: string[]; weights?: Record<string, number> };
  const criteriaIds = Array.isArray(config.criteriaIds) ? config.criteriaIds : [];

  const [items, attributes, cycle, track, annotations, overrides, artifacts, transcript] = await Promise.all([
    // Only workspace items live in `items`; a typed item's id (item_9rbxi1e) is
    // not a uuid, and one such id makes Postgres refuse the whole lookup.
    allIds.some(isUuid)
      ? db.from('items').select('id, title').in('id', allIds.filter(isUuid))
      : Promise.resolve({ data: [] as Array<{ id: string; title: string }> }),
    criteriaIds.length ? (session.workspace_id
      ? db.from('attributes').select('key, label, workspace_id').in('key', criteriaIds).or(`workspace_id.eq.${session.workspace_id},workspace_id.is.null`)
      : db.from('attributes').select('key, label, workspace_id').in('key', criteriaIds).is('workspace_id', null)) : Promise.resolve({ data: [] as Array<{ key: string; label: string; workspace_id: string | null }> }),
    session.cycle_id ? db.from('degap_cycles').select('name').eq('id', session.cycle_id).maybeSingle() : Promise.resolve({ data: null }),
    session.track_id ? db.from('tracks').select('name').eq('id', session.track_id).maybeSingle() : Promise.resolve({ data: null }),
    // Confirmed notes only: a transcript passage the AI proposed counts once a person confirms it (mig 831).
    db.from('deliberation_item_annotations').select('item_id, author_id, reviewed_by, content, source, transcript_timestamp, created_at').eq('session_id', sessionId).is('retracted_at', null).eq('status', 'confirmed').order('created_at'),
    db.from('deliberation_override_justifications').select('item_id, overridden_by, reason, aggregate_result, override_result, created_at').eq('session_id', sessionId).order('created_at'),
    db.from('session_artifacts').select('artifact_type, title, url, content, storage_path, created_at').eq('session_id', sessionId).order('created_at'),
    loadSessionTranscript(sessionId),
  ]);

  const authorIds = [
    ...new Set([
      ...(annotations.data ?? []).map((a) => a.author_id),
      ...(annotations.data ?? []).map((a) => a.reviewed_by),
      ...(overrides.data ?? []).map((o) => o.overridden_by),
    ].filter((v): v is string => typeof v === 'string')),
  ];
  const { data: people } = authorIds.length
    ? await db.from('users').select('id, name, email').in('id', authorIds)
    : { data: [] as Array<{ id: string; name: string | null; email: string | null }> };
  const nameOf = (id: string | null | undefined) => {
    if (!id) return null;
    const p = (people ?? []).find((u) => u.id === id);
    return p?.name?.trim() || p?.email?.trim() || null;
  };

  const labelByKey = new Map<string, string>();
  for (const a of attributes.data ?? []) {
    // A workspace's own label wins over a shared one.
    if (!labelByKey.has(a.key) || a.workspace_id === session.workspace_id) labelByKey.set(a.key, a.label);
  }

  const rankOf = (v: unknown): number | null => {
    if (typeof v === 'number') return v;
    if (v && typeof v === 'object' && typeof (v as { rank?: unknown }).rank === 'number') return (v as { rank: number }).rank;
    return null;
  };

  const transcriptText = transcript.text;

  // The method's name as the app shows it everywhere else; a stored id is never shown raw.
  const toolId = session.selected_tool_id ?? output.toolId ?? null;
  const toolName =
    resolveSessionToolDescriptor(toolId)?.name ??
    (toolId ? getToolById(toolId)?.name : undefined) ??
    (output.toolName && output.toolName !== toolId ? output.toolName : null) ??
    (toolId ? humanizeCriterionId(toolId) : null);

  return {
    session: {
      id: session.id,
      name: session.name,
      workspaceId: session.workspace_id,
      cycleName: (cycle.data as { name?: string } | null)?.name ?? null,
      trackName: (track.data as { name?: string } | null)?.name ?? null,
      toolName,
      publishedAt: session.published_at,
      participantCount: session.participant_ids?.length ?? 0,
    },
    criteria: criteriaIds.map((id) => ({
      id,
      label: labelByKey.get(id) ?? humanizeCriterionId(id),
      weight: typeof config.weights?.[id] === 'number' ? config.weights[id] : null,
    })),
    ranking,
    cutLine,
    // The session's own names (typed items, criteria, isolated lines), then the
    // workspace's current titles over them.
    itemTitles: {
      ...sessionOwnItemTitles({ modeConfig: session.mode_config, isolatedItems: session.isolated_items }),
      ...Object.fromEntries((items.data ?? []).map((i) => [i.id, i.title])),
    },
    itemNotes: Object.fromEntries(Object.entries(output.itemNotes ?? {}).filter(([id]) => coveredIds.includes(id))),
    annotations: (annotations.data ?? []).map((a) => ({
      itemId: a.item_id,
      // A transcript passage is credited to the person who confirmed it; the AI that found it is not a person.
      authorName: a.source === 'transcript_excerpt' ? nameOf(a.reviewed_by ?? a.author_id) : nameOf(a.author_id),
      text: a.content,
      at: a.created_at,
      source: a.source,
      transcriptTimestamp: a.transcript_timestamp,
    })),
    overrides: (overrides.data ?? []).map((o) => ({
      itemId: o.item_id,
      byName: nameOf(o.overridden_by),
      reason: o.reason,
      fromRank: rankOf(o.aggregate_result),
      toRank: rankOf(o.override_result),
      at: o.created_at,
    })),
    documents: (artifacts.data ?? []).map((a) => ({
      title: a.title,
      type: a.artifact_type,
      hasFile: Boolean(a.storage_path),
      url: a.url,
    })),
    transcriptText,
  };
}

export interface WriteMemoResult {
  ok: boolean;
  artifact?: ReportArtifact;
  /** Plain words, shown to the person. */
  error?: string;
}

/**
 * Write the prioritization memo for a session and save it as a report artifact
 * (template_slug 'prioritization_memo'). Each call writes a new one; the newest
 * is the memo. Never throws: publishing must not wait on, or fail because of,
 * the memo (owner ruling K9).
 */
export async function writePrioritizationMemo(
  sessionId: string,
  options: { actorId?: string | null } = {},
): Promise<WriteMemoResult> {
  try {
    const sources = await loadMemoSources(sessionId);
    if (!sources) return { ok: false, error: 'This session has no ranking to explain yet.' };

    const { above, nearMisses } = splitAtCutLine(sources.ranking, sources.cutLine);
    const { raw, note } = await explainForMemo(buildMemoPrompt(sources), sources.session.workspaceId);
    const explanation = parseMemoExplanation(raw, [...above, ...nearMisses].map((r) => r.itemId));
    const content = assembleMemo(
      sources,
      explanation,
      explanation ? null : note ?? 'The AI answer could not be read, so this memo carries only what was recorded. Regenerate to try again.',
    );

    let actor = options.actorId ?? null;
    if (!actor) {
      const { data } = await getTypedSupabaseClient().auth.getUser();
      actor = data.user?.id ?? null;
    }

    const artifact: ReportArtifact = {
      id: generateUUID(),
      sessionId,
      templateSlug: MEMO_TEMPLATE_SLUG,
      workspaceId: sources.session.workspaceId ?? undefined,
      title: `Prioritization memo — ${sources.session.name}`,
      content: content as unknown as Record<string, unknown>,
      generatedAt: new Date(),
      generatedBy: actor ?? 'system',
      status: 'draft',
    };
    const persistError = await persistReportArtifact(artifact);
    if (persistError) return { ok: false, error: `The memo was written but could not be saved: ${persistError}` };
    return { ok: true, artifact };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : 'The memo could not be written.' };
  }
}

/** The newest memo for a session, or null. */
export async function fetchLatestMemo(sessionId: string): Promise<ReportArtifact | null> {
  const all = await fetchArtifactsForSession(sessionId);
  return all.find((a) => a.templateSlug === MEMO_TEMPLATE_SLUG) ?? null;
}

// ── Template fetcher ──────────────────────────────────────────────────────────

/**
 * Fetch report templates for the current workspace.
 * Always includes the three system templates.
 * Falls back to client-side system templates if Supabase is unavailable.
 */
export async function fetchReportTemplates(workspaceId?: string): Promise<ReportTemplate[]> {
  if (!isSupabaseAvailable()) return SYSTEM_REPORT_TEMPLATES;

  const supabase = getSafeSupabaseClient();
  if (!supabase) return SYSTEM_REPORT_TEMPLATES;

  try {
    const { data, error } = await supabase
      .from('report_templates')
      .select('*')
      .or(`workspace_id.is.null${workspaceId ? `,workspace_id.eq.${workspaceId}` : ''}`)
      .eq('status', 'active')
      .order('is_system', { ascending: false })
      .order('created_at', { ascending: true });

    if (error || !data) {
      console.warn('[reportGenerationService] Template fetch failed, using built-ins:', error?.message);
      return SYSTEM_REPORT_TEMPLATES;
    }

    return data.map((row: Record<string, unknown>) => ({
      id: row.id as string,
      name: row.name as string,
      slug: row.slug as string,
      description: row.description as string | undefined,
      audience: (row.audience as string) ?? 'all',
      tone: (row.tone as string) ?? 'professional',
      isSystem: (row.is_system as boolean) ?? false,
      sections: (row.sections as ReportSection[]) ?? [],
    }));
  } catch (e) {
    console.warn('[reportGenerationService] Exception fetching templates, using built-ins:', e);
    return SYSTEM_REPORT_TEMPLATES;
  }
}

/**
 * Fetch a single report artifact by id.
 */
export async function fetchReportArtifact(artifactId: string): Promise<ReportArtifact | null> {
  if (!isSupabaseAvailable()) return null;
  const supabase = getSafeSupabaseClient();
  if (!supabase) return null;

  const { data, error } = await supabase
    .from('report_artifacts')
    .select('*')
    .eq('id', artifactId)
    .single();

  if (error || !data) return null;

  return {
    id: data.id,
    sessionId: data.session_id,
    templateId: data.template_id ?? undefined,
    templateSlug: data.template_slug,
    workspaceId: data.workspace_id ?? undefined,
    title: data.title,
    content: data.content ?? {},
    generatedAt: new Date(data.generated_at),
    generatedBy: data.generated_by,
    status: data.status,
  };
}

/**
 * Fetch all report artifacts for a session.
 */
export async function fetchArtifactsForSession(sessionId: string): Promise<ReportArtifact[]> {
  if (!isSupabaseAvailable()) return [];
  const supabase = getSafeSupabaseClient();
  if (!supabase) return [];

  const { data, error } = await supabase
    .from('report_artifacts')
    .select('*')
    .eq('session_id', sessionId)
    .order('generated_at', { ascending: false });

  if (error || !data) return [];

  return data.map((row: Record<string, unknown>) => ({
    id: row.id as string,
    sessionId: row.session_id as string,
    templateId: (row.template_id as string | null) ?? undefined,
    templateSlug: row.template_slug as string,
    workspaceId: (row.workspace_id as string | null) ?? undefined,
    title: row.title as string,
    content: (row.content as Record<string, unknown>) ?? {},
    generatedAt: new Date(row.generated_at as string),
    generatedBy: row.generated_by as string,
    status: row.status as 'draft' | 'published',
  }));
}
